import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Custom delete-confirmation dialog (replaces window.confirm calls so the
 * UX matches the rest of the app, per user request: "Anywhere there is a
 * confirm to delete action, it should be custom UI.")
 *
 * Use via the imperative helper `confirmDelete({ title, message, confirmLabel })`
 * which returns a Promise<boolean>.
 */

let _setOpen = null;
let _resolve = null;

export function confirmDelete({ title = "Are You Sure?", message = "This action can't be undone.", confirmLabel = "Delete", checkboxLabel = "I understand this cannot be undone" } = {}) {
  return new Promise((resolve) => {
    if (!_setOpen) {
      resolve(window.confirm(`${title}\n${message}`));
      return;
    }
    _resolve = resolve;
    _setOpen({ open: true, title, message, confirmLabel, checkboxLabel });
  });
}

export function ConfirmDialogHost() {
  const [state, setState] = useState({ open: false, title: "", message: "", confirmLabel: "Delete", checkboxLabel: null });
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    _setOpen = (s) => { setState(s); setChecked(false); };
    return () => { _setOpen = null; };
  }, []);
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.open]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = (val) => {
    setState((s) => ({ ...s, open: false }));
    if (_resolve) {
      _resolve(state.checkboxLabel ? (val && checked ? "checked" : val) : val);
      _resolve = null;
    }
  };
  if (!state.open) return null;
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center"
      onClick={() => close(false)}
      data-testid="confirm-dialog-overlay"
    >
      <div
        className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-5 max-w-sm w-full mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="confirm-dialog"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-sm font-semibold text-[#EDEDED]">{state.title}</h3>
            <p className="text-xs text-[#A1A1AA] mt-1">{state.message}</p>
          </div>
          <button
            onClick={() => close(false)}
            className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] flex-shrink-0"
            data-testid="confirm-dialog-x"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {state.checkboxLabel && (
          <label className="flex items-center gap-2 px-3 py-2 mb-2 rounded-md border border-[#2A2A2A] bg-[#0A0A0A] cursor-pointer hover:border-[#3A3A3A]">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              data-testid="confirm-dialog-checkbox"
            />
            <span className="text-xs text-[#EDEDED]">{state.checkboxLabel}</span>
          </label>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={() => close(false)}
            className="px-3 py-1.5 text-xs text-[#A1A1AA] hover:bg-[#1a1a1a] rounded"
            data-testid="confirm-dialog-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => close(true)}
            disabled={!!state.checkboxLabel && !checked}
            className={`px-3 py-1.5 text-xs rounded bg-[#FF3B30] text-white font-medium transition-opacity ${!!state.checkboxLabel && !checked ? "opacity-40 cursor-not-allowed" : "hover:bg-[#FF453A]"}`}
            data-testid="confirm-dialog-confirm"
            autoFocus
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// (no extra react import — useState/useEffect imported at the top)
